import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import ChatRetrievalStrategySection, {
    chatRetrievalStrategyFromMetadata,
    classicMaxPriorUserLinesFromMetadata,
    DEFAULT_CLASSIC_MAX_PRIOR_USER_LINES,
} from './ChatRetrievalStrategySection';
import RagClassicPriorLinesField from './RagClassicPriorLinesField';
import RagQueryRewriteAdvancedOptions from './RagQueryRewriteAdvancedOptions';
import useCloudProviders from '../../hooks/useCloudProviders';
import useLlmModelPicker from '../../hooks/useLlmModelPicker';

/**
 * Chat retrieval UI for a vector/hybrid collection. Does not persist by itself:
 * parent calls ref.appendRagToFormData(fd) and sends one updateDataCollection request.
 */
const CollectionChatSearchSection = forwardRef(function CollectionChatSearchSection(
    { collection, keycloak, navigate, disabled = false },
    ref,
) {
    const [retrievalStrategy, setRetrievalStrategy] = useState('simple');
    const [classicMaxPriorUserLines, setClassicMaxPriorUserLines] = useState(DEFAULT_CLASSIC_MAX_PRIOR_USER_LINES);
    const [ragRewriteMax, setRagRewriteMax] = useState(10);

    const isVectorOrHybrid = collection?.indexing_method === 'vector' || collection?.indexing_method === 'hybrid';
    const { cloudProviders } = useCloudProviders(keycloak, { enabled: isVectorOrHybrid });

    const rewritePicker = useLlmModelPicker({
        keycloak,
        cloudProviders,
        enabled: isVectorOrHybrid && retrievalStrategy === 'query_rewrite',
    });

    const judgePicker = useLlmModelPicker({
        keycloak,
        cloudProviders,
        enabled: isVectorOrHybrid,
    });

    useImperativeHandle(ref, () => ({
        appendRagToFormData(fd) {
            fd.append('rag_query_rewrite_mode', retrievalStrategy === 'query_rewrite' ? 'true' : 'false');
            fd.append('rag_semantic_dual_query_mode', retrievalStrategy === 'dual_semantic' ? 'true' : 'false');
            if (retrievalStrategy === 'simple') {
                fd.append('rag_classic_max_prior_user_lines', String(classicMaxPriorUserLines));
            }
            if (retrievalStrategy === 'query_rewrite') {
                fd.append('rag_query_rewrite_max_messages', String(ragRewriteMax));
                if (rewritePicker.source === 'cloud') {
                    fd.append('rag_query_rewrite_provider_id', rewritePicker.providerId != null ? String(rewritePicker.providerId) : '');
                    fd.append('rag_query_rewrite_model', rewritePicker.model.trim());
                } else {
                    fd.append('rag_query_rewrite_provider_id', '');
                    fd.append('rag_query_rewrite_model', rewritePicker.model.trim());
                }
            }
            if (judgePicker.source === 'cloud') {
                fd.append('rag_judge_provider_id', judgePicker.providerId != null ? String(judgePicker.providerId) : '');
                fd.append('rag_judge_model', judgePicker.model.trim());
            } else {
                fd.append('rag_judge_provider_id', '');
                fd.append('rag_judge_model', judgePicker.model.trim());
            }
        },
    }), [
        retrievalStrategy,
        classicMaxPriorUserLines,
        ragRewriteMax,
        rewritePicker.source,
        rewritePicker.providerId,
        rewritePicker.model,
        judgePicker.source,
        judgePicker.providerId,
        judgePicker.model,
    ]);

    useEffect(() => {
        const col = collection;
        if (!col) return;
        const dm = col.document_metadata && typeof col.document_metadata === 'object' ? col.document_metadata : {};
        setRetrievalStrategy(chatRetrievalStrategyFromMetadata(dm));
        setClassicMaxPriorUserLines(classicMaxPriorUserLinesFromMetadata(dm));
        const n = dm.rag_query_rewrite_max_messages;
        setRagRewriteMax(typeof n === 'number' && !Number.isNaN(n) ? n : 10);
        const pid = dm.rag_query_rewrite_provider_id;
        rewritePicker.hydrateFrom({
            is_local: pid == null,
            model: dm.rag_query_rewrite_model || '',
            cloud_provider_id: pid,
        });
        const jpid = dm.rag_judge_provider_id;
        judgePicker.hydrateFrom({
            is_local: jpid == null,
            model: dm.rag_judge_model || '',
            cloud_provider_id: jpid,
        });
    }, [collection?.id, collection?.document_metadata]);

    if (!isVectorOrHybrid) {
        return null;
    }

    return (
        <div className="collection-detail-chat-search-form edit-collection-chat-search-form">
            <ChatRetrievalStrategySection
                compact
                value={retrievalStrategy}
                onChange={setRetrievalStrategy}
                disabled={disabled}
            >
                {retrievalStrategy === 'simple' && (
                    <RagClassicPriorLinesField
                        value={classicMaxPriorUserLines}
                        onChange={setClassicMaxPriorUserLines}
                        disabled={disabled}
                    />
                )}
                {retrievalStrategy === 'query_rewrite' && (
                    <div className="create-collection-rag-rewrite-advanced">
                        <RagQueryRewriteAdvancedOptions
                            compact
                            maxMessages={ragRewriteMax}
                            onMaxMessagesChange={setRagRewriteMax}
                            picker={rewritePicker}
                            cloudProviders={cloudProviders}
                            navigate={navigate}
                            disabled={disabled}
                            hideSaveButton
                        />
                    </div>
                )}
            </ChatRetrievalStrategySection>
            <div className="create-collection-rag-rewrite-advanced" style={{ marginTop: 16 }}>
                <RagQueryRewriteAdvancedOptions
                    compact
                    hideMaxMessages
                    sectionTitle="Answer quality judge (optional)"
                    modelStepLabel="Custom judge model (leave empty for platform default: openrouter/qwen/qwen3.5-flash-02-23)"
                    maxMessages={10}
                    onMaxMessagesChange={() => {}}
                    picker={judgePicker}
                    cloudProviders={cloudProviders}
                    navigate={navigate}
                    disabled={disabled}
                    hideSaveButton
                />
            </div>
        </div>
    );
});

export default CollectionChatSearchSection;
